import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { HandCoins, Search } from "lucide-react";
import { api, money } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { formatPhone } from "@/lib/phone";

export default function ActiveLoans() {
  const { branch } = useAuth();
  const [allBranches, setAllBranches] = useState(false);
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/loans/active", { params: allBranches ? {} : { branch } })
      .then(({ data: d }) => setData(d))
      .catch(() => toast.error("Could not load the loans"))
      .finally(() => setLoading(false));
  }, [allBranches, branch]);

  useEffect(load, [load]);

  const term = q.trim().toLowerCase();
  const rows = (data?.rows || []).filter(
    (r) =>
      !term ||
      String(r.customer_name || "").toLowerCase().includes(term) ||
      String(r.customer_phone || "").includes(term) ||
      String(r.doc_number || "").includes(term)
  );
  const t = data?.totals;

  return (
    <div className="fade-up space-y-7" data-testid="active-loans-page">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#B38F4E]">
          {allBranches ? "BOTH STORES" : branch}
        </p>
        <h1 className="mt-2 text-3xl font-bold sm:text-4xl">Active Loans</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Every pledge loan still outstanding, with interest worked out to today.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-px border border-border bg-border sm:grid-cols-2 xl:grid-cols-5">
        {[
          ["Loans open", String(t?.loans ?? 0), "loans"],
          ["Principal out", money(t?.principal || 0), "principal"],
          ["Interest owing", money(t?.interest_outstanding || 0), "interest"],
          ["Total due", money(t?.total_due || 0), "due"],
          ["Overdue", String(t?.overdue ?? 0), "overdue"],
        ].map(([label, value, key]) => (
          <div key={key} className="bg-white p-5" data-testid={`active-loans-stat-${key}`}>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
              {label}
            </div>
            <div className="tabular mt-3 text-2xl font-bold">{value}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-4 border border-border p-4">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="h-10 rounded-none pl-9"
            placeholder="Search name, phone or loan number"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="active-loans-search"
          />
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={allBranches}
            onChange={(e) => setAllBranches(e.target.checked)}
            className="accent-[#C5A059]"
            data-testid="active-loans-all-branches"
          />
          Include both stores
        </label>
      </div>

      <div className="overflow-x-auto border border-border">
        <table className="w-full min-w-[980px] text-sm" data-testid="active-loans-table">
          <thead className="bg-[#0A0A0A] text-white">
            <tr>
              {["Loan", "Customer", "Pledged", "Principal", "Rate", "Months",
                "Interest owing", "Monthly due", "Total due", "Due date"].map((h) => (
                <th key={h} className="px-3 py-2.5 text-left font-semibold">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={10} className="px-3 py-12 text-center text-muted-foreground">
                  <HandCoins className="mx-auto mb-2 h-5 w-5 text-[#C5A059]" />
                  No loans outstanding.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr
                key={r.id}
                className={`hover:bg-[#FBF8F2] ${r.overdue ? "bg-[#FDF2F2]" : ""}`}
                data-testid={`active-loan-row-${r.doc_number}`}
              >
                <td className="tabular px-3 py-2.5 font-semibold">
                  <Link to={`/documents/${r.id}`} className="underline decoration-[#C5A059]">
                    #{r.doc_number}
                  </Link>
                </td>
                <td className="px-3 py-2.5">
                  {r.customer_name}
                  <span className="block text-xs text-muted-foreground">{formatPhone(r.customer_phone)}</span>
                </td>
                <td className="px-3 py-2.5 text-xs">
                  {r.description || "-"}
                  {r.weight ? <span className="block text-muted-foreground">{r.weight}</span> : null}
                </td>
                <td className="tabular px-3 py-2.5">{money(r.principal_outstanding)}</td>
                <td className="tabular px-3 py-2.5">{r.interest_rate}%</td>
                <td className="tabular px-3 py-2.5">{r.months_elapsed}</td>
                <td className="tabular px-3 py-2.5">{money(r.interest_outstanding)}</td>
                <td className="tabular px-3 py-2.5">{money(r.monthly_due)}</td>
                <td className="tabular px-3 py-2.5 font-bold">{money(r.total_due)}</td>
                <td className="tabular px-3 py-2.5">
                  {r.end_date || "-"}
                  {r.days_left !== null && (
                    <span
                      className={`block text-xs ${
                        r.overdue ? "font-semibold text-[#A11B1B]" : "text-muted-foreground"
                      }`}
                    >
                      {r.overdue ? `${Math.abs(r.days_left)} days overdue` : `${r.days_left} days left`}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
